import { Zap } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg";

const sizes: Record<Size, { box: string; icon: string; text: string }> = {
  sm: { box: "h-9 w-9 rounded-lg", icon: "h-5 w-5", text: "text-lg" },
  md: { box: "h-11 w-11 rounded-lg", icon: "h-6 w-6", text: "text-2xl" },
  lg: { box: "h-14 w-14 rounded-xl", icon: "h-7 w-7", text: "text-3xl" },
};

export function BrandMark({
  size = "sm",
  showName = true,
  className,
}: {
  size?: Size;
  showName?: boolean;
  className?: string;
}) {
  const { brand } = useAppSettings();
  const s = sizes[size];
  const name = brand.name || "StoreSEO AI";

  // Render Supabase-hosted brand logos through the on-the-fly image
  // transformation endpoint so we don't ship a 1000+ KiB original
  // for a tiny on-screen logo. Falls back to the raw URL for any
  // non-Supabase asset.
  const renderPx = size === "lg" ? 112 : size === "md" ? 88 : 72; // ~2x display size for retina
  const optimizedLogoUrl = brand.logoUrl
    ? brand.logoUrl.includes("/storage/v1/object/public/")
      ? `${brand.logoUrl.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/")}?width=${renderPx}&height=${renderPx}&resize=cover&quality=80`
      : brand.logoUrl
    : null;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div
        className={cn(
          "bg-gradient-primary flex items-center justify-center shadow-glow shrink-0 overflow-hidden",
          s.box
        )}
      >
        {optimizedLogoUrl ? (
          <img
            src={optimizedLogoUrl}
            alt={name}
            width={renderPx}
            height={renderPx}
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
          />
        ) : (
          <Zap className={cn("text-primary-foreground", s.icon)} />
        )}
      </div>
      {showName && <span className={cn("font-bold", s.text)}>{name}</span>}
    </div>
  );
}
